import React, { memo, useCallback } from 'react'

import {
  Button, classnames, Icon, RotatingSpinner, Trash,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import useImageUploader from 'Pods/ThreeDStudio/Shared/ActionSelector/Hooks/useImageUploader'

const trashIconSize = { width: '14px', height: '14px' }
const spinnerIconSize = { width: '28px', height: '28px' }
const trashIcon = <Icon svgComponent={Trash} color="dark" size={trashIconSize} notSpaced />
const spinnerIcon = <Icon svgComponent={RotatingSpinner} size={spinnerIconSize} color="primary" notSpaced />
// Multi-view uploads are kept apart from the single reference image, so none of them is
// pushed onto the Edit tab.
const UPLOADER_OPTIONS = { feedsIdeationFlow: false }

function ViewSlot({
  viewType,
  label,
  imageUrl = undefined,
  setInputViewImage,
  storeSchemaKey,
  sliceName,
  required = false,
  disabled = false,
}) {
  const t = useTranslation()
  const setImage = useCallback((id, url) => {
    setInputViewImage(viewType, id, url)
  }, [setInputViewImage, viewType])

  const {
    inputRef,
    allowedTypes,
    isLoading,
    isUploading,
    onDragOver,
    onDrop,
    onFileChange,
    onFileClick,
    handleRemovePreview,
  } = useImageUploader(setImage, storeSchemaKey, sliceName, UPLOADER_OPTIONS)

  const canAcceptFile = !isLoading && !disabled
  const showPreview = !!imageUrl && !isUploading
  const showPlaceholder = !imageUrl && !isUploading

  return (
    <div
      className={classnames(
        'multi-view-uploader__slot',
        showPreview && 'multi-view-uploader__slot--filled',
        isUploading && 'multi-view-uploader__slot--loading',
        disabled && 'multi-view-uploader__slot--disabled',
      )}
      onDragOver={onDragOver}
      onDrop={canAcceptFile ? onDrop : undefined}
      aria-busy={isUploading}
    >
      <input
        ref={inputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={canAcceptFile ? onFileChange : undefined}
        className="multi-view-uploader__input"
        aria-hidden
        tabIndex={-1}
        disabled={!canAcceptFile}
      />
      {isUploading && <div className="multi-view-uploader__loading">{spinnerIcon}</div>}
      {showPreview && (
        <div className="multi-view-uploader__preview-wrap">
          <img
            src={imageUrl}
            alt={t('shared.actionSelector.imageUploader.multiView.preview', { view: label })}
            className="multi-view-uploader__preview"
            decoding="async"
          />
          <Button
            type="secondary"
            variant="only-icon"
            size="tiny"
            icon={trashIcon}
            customClass="multi-view-uploader__remove"
            onClick={handleRemovePreview}
            aria-label={t('shared.actionSelector.imageUploader.multiView.remove', { view: label })}
          />
        </div>
      )}
      {showPlaceholder && required && (
        <span className="multi-view-uploader__required-tag">
          {t('shared.actionSelector.imageUploader.multiView.required')}
        </span>
      )}
      {showPlaceholder && (
        <button
          type="button"
          className="multi-view-uploader__trigger"
          onClick={onFileClick}
          disabled={!canAcceptFile}
          aria-label={t('shared.actionSelector.imageUploader.multiView.upload', { view: label })}
        >
          <img
            src={`/assets/three_d_studio/multi_view_uploader/${viewType}.svg`}
            alt=""
            aria-hidden="true"
            className="multi-view-uploader__placeholder-icon"
          />
          <span className="multi-view-uploader__label cgt-caption-semibold">{label}</span>
        </button>
      )}
    </div>
  )
}

ViewSlot.propTypes = {
  viewType: string.isRequired,
  label: string.isRequired,
  imageUrl: string,
  setInputViewImage: func.isRequired,
  storeSchemaKey: string.isRequired,
  sliceName: string.isRequired,
  required: bool,
  disabled: bool,
}

export default memo(ViewSlot)
